import {
  IConfiguration, IEraConfiguration,
} from "../../../domain/configuration";
import {
  ObjectUtility, AsyncUtility,
} from "../../../utilities";
import store from "../../../store";
import { HttpRepository } from "../..";
import {
  IConfigurationRepository,
  DefaultConfigurationFactory,
} from "..";
import { IEraConfigurationBean } from "./beans";
import { LoggerFactory } from "../../../utilities/logger";

export class ConfigurationRepository<TConfiguration extends IConfiguration = IConfiguration> extends HttpRepository implements IConfigurationRepository<TConfiguration> {
  private readonly logger = LoggerFactory.getLogger();

  constructor(overwriteDefaultConfiguration?: (s: TConfiguration) => void) {
    super();
    this._overwriteDefaultConfiguration = overwriteDefaultConfiguration
      ? overwriteDefaultConfiguration
      : s => {};
  }

  private readonly _overwriteDefaultConfiguration: (s: TConfiguration) => void;

  public async getConfiguration(): Promise<TConfiguration> {
    const hostName = store.getters.hostName;
    const prefetchedData = (window as any).prefetchedData;
    if (prefetchedData) {
      if (!prefetchedData.configuration) {
        // プリフェッチの受信後のスレッドにまだ切り替わっていないと値が格納されないので、
        // JavaScriptのスレッドをいったん切り替えている
        await AsyncUtility.sleep(10);
      }
      const prefetched = prefetchedData.configuration;
      if (prefetched && prefetched.value && this.isSameText(prefetched.hostName, hostName)) {
        this.logger.info("プリフェッチされた設定を使用します。");
        return this.getMergedConfiguration(prefetched.value.system, prefetched.value.terminal);
      }
    }
    return (hostName && (hostName !== ""))
      ? this.getHostConfiguration(hostName)
      : this.getSystemConfiguration();
  }

  private isSameText(left: string, right: string): boolean {
    if (this.isEmptyText(left) && this.isEmptyText(right)) { return true; }
    return (left === right);
  }

  private isEmptyText(text: string): boolean {
    return (text === null || text === undefined || text === "");
  }

  public getDefaultConfiguration(): TConfiguration {
    const result = DefaultConfigurationFactory.create() as TConfiguration;
    this._overwriteDefaultConfiguration(result);
    return result;
  }

  public async getSystemConfiguration(): Promise<TConfiguration> {
    const result = await this.get("/api/settings/system/get");
    const configuration = result.data;
    if (this.shouldOverwriteEraInformations(configuration)) {
      const eraConfigurations = await this.getEraConfigurations();
      this.mergeEraConfigurations(configuration, eraConfigurations);
    }
    return configuration;
  }

  private shouldOverwriteEraInformations(system: TConfiguration, terminal: TConfiguration = null): boolean {
    if (terminal && terminal.system && terminal.system.date && terminal.system.date.shouldOverwriteEras !== undefined) {
      // 端末設定で指定されている場合はその値で制御する
      return !!terminal.system.date.shouldOverwriteEras;
    }
    // まだDefaultConfigurationはマージされていない
    return (system && system.system && system.system.date && system.system.date.shouldOverwriteEras !== undefined)
      ? system.system.date.shouldOverwriteEras
      : false;
  }

  private async getEraConfigurations(): Promise<IEraConfiguration[]> {
    try {
      const result = await this.get("/api/settings/japanese-era-Info");
      return this.toEraConfigurations(result.data);
    } catch {
      this.logger.error("和暦設定の取得に失敗しました.");
    }
    return null;
  }

  private toEraConfigurations(beans: IEraConfigurationBean[]): IEraConfiguration[] {
    if (!beans) { return null; }
    if (beans.length === 0) {
      // バックエンドでレジストリから取得失敗しているケースへの対応(念のため)
      throw new Error("beans is empty!!!");
    }
    return beans
      .map(bean => this.toEraConfiguration(bean))
      .sort((a, b) => b.origin.year - a.origin.year);
  }

  private toEraConfiguration(bean: IEraConfigurationBean): IEraConfiguration {
    if (!bean) { throw new Error("bean is empty!!!"); }
    // バックエンドでレジストリから取得失敗しているケースへの対応(念のため)
    // 現状では同じinterfaceを備えているので、変換する必要は(今のところは)無し
    if (this.isEmpty(bean.name)) { throw new Error("name is empty!!!"); }
    if (this.isEmpty(bean.shortName)) { throw new Error("shortName is empty!!!"); }
    if (this.isEmpty(bean.initial)) { throw new Error("initial is empty!!!"); }
    if (this.isEmpty(bean.origin.year)) { throw new Error("origin year is empty!!!"); }
    if (this.isEmpty(bean.origin.month)) { throw new Error("origin month is empty!!!"); }
    if (this.isEmpty(bean.origin.day)) { throw new Error("origin day is empty!!!"); }
    return bean;
  }

  private isEmpty(value: any): boolean {
    return (value === null) || (value === undefined) || (value === "");
  }

  private mergeEraConfigurations(configuration: TConfiguration, eraConfigurations: IEraConfiguration[]): void {
    if (!configuration || !eraConfigurations) { return; }
    try {
      configuration.system.date.japaneseEras = eraConfigurations;
    } catch {
      this.logger.error("和暦設定のマージに失敗しました.");
    }
  }

  private async getHostConfiguration(hostName: string): Promise<TConfiguration> {
    const result = await this.get(
      "/api/settings/terminal/get",
      {
        params: {
          hostName,
        },
      });
    return this.getMergedConfiguration(result.data.system, result.data.terminal);
  }
  
  private async getMergedConfiguration(system: TConfiguration, terminal: TConfiguration): Promise<TConfiguration> {
    if (this.shouldOverwriteEraInformations(system, terminal)) {
      const eraConfigurations = await this.getEraConfigurations();
      this.mergeEraConfigurations(system, eraConfigurations);
    }
    if (!system) {
      this.logger.error("system設定が取得できません.DBの設定値を確認してください.(不要な「,」が付いてないか等)");
      return null;
    } else if (!terminal) {
      this.logger.info("terminal設定が取得できません.システム設定が利用されます.");
      return system;
    } else {
      return ObjectUtility.mergeDeeply(system, terminal);
    }
  }

  public async getTerminalConfiguration(hostName: string): Promise<TConfiguration> {
    const result = await this.get(
      "/api/settings/terminal/get",
      {
        params: {
          hostName,
        },
      });
    return result.data.terminal;
  }

  public async getAssemblyVer(): Promise<string> {
    const url = "/api/settings/assembly/get";
    const result = await this.get(url);
    console.log("assembly " + result.data);
    return result.data;
  }

}
